import { Activity, ArrowDownRight, ArrowUpRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { TrafficOverviewSummary } from '../../services/dashboardSummary.service';

interface TrafficOverviewCardProps {
    data: TrafficOverviewSummary | null;
    loading: boolean;
    bandwidthTimeline?: Array<{ timestamp: Date; bytes: number; bandwidth_mbps: number }>;
}

const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const formatNumber = (num: number): string => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
};

// Mbps can be 0.0008 for fleets with only management traffic; always
// showing "0.0 Mbps" obscures that the pipeline is actually live. Pick
// the smallest unit that gives a non-zero reading.
const formatBandwidthMbps = (mbps: number): { value: string; unit: string } => {
    if (mbps >= 1000) return { value: (mbps / 1000).toFixed(2), unit: 'Gbps' };
    if (mbps >= 1) return { value: mbps.toFixed(2), unit: 'Mbps' };
    if (mbps >= 0.001) return { value: (mbps * 1000).toFixed(1), unit: 'Kbps' };
    if (mbps > 0) return { value: (mbps * 1_000_000).toFixed(0), unit: 'bps' };
    return { value: '0', unit: 'bps' };
};

// Mini sparkline component
const Sparkline = ({ data, width = 120, height = 32 }: { data: number[]; width?: number; height?: number }) => {
    if (data.length === 0) return null;

    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;

    const points = data.map((value, index) => {
        const x = (index / (data.length - 1)) * width;
        const y = height - ((value - min) / range) * height;
        return `${x},${y}`;
    });

    const pathD = `M ${points.join(' L ')}`;

    // Area fill
    const areaD = `M 0,${height} L ${pathD.replace('M ', '')} L ${width},${height} Z`;

    return (
        <svg width={width} height={height} className="overflow-visible">
            <defs>
                <linearGradient id="sparklineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
                    <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaD} fill="url(#sparklineGradient)" />
            <path d={pathD} fill="none" stroke="rgb(59, 130, 246)" strokeWidth="2" />
            {/* Current value dot */}
            {data.length > 0 && (
                <circle
                    cx={width}
                    cy={height - ((data[data.length - 1] - min) / range) * height}
                    r="3"
                    fill="rgb(59, 130, 246)"
                />
            )}
        </svg>
    );
};

export const TrafficOverviewCard = ({ data, loading, bandwidthTimeline }: TrafficOverviewCardProps) => {
    if (loading) {
        return (
            <div className="card animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="h-16 bg-gray-200 rounded"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                </div>
                <div className="h-8 bg-gray-200 rounded w-full"></div>
            </div>
        );
    }

    if (!data || (data.totalFlows === 0 && data.totalBytes === 0)) {
        return (
            <div className="card">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Network Traffic</h2>
                    <Link
                        to="/analytics/traffic"
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                        View Details →
                    </Link>
                </div>
                <div className="flex flex-col items-center justify-center py-6 text-center">
                    <Activity className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500">No traffic data available</p>
                    <p className="text-xs text-gray-400 mt-1">Traffic analytics will appear here</p>
                </div>
            </div>
        );
    }

    // Use real bandwidth timeline data only — no mock data
    const sparklineData = bandwidthTimeline && bandwidthTimeline.length > 1
        ? bandwidthTimeline.map(t => t.bandwidth_mbps)
        : null;

    return (
        <div className="card">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Network Traffic</h2>
                <Link
                    to="/analytics/traffic"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                    View Details →
                </Link>
            </div>

            {/* Main Stats Row */}
            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Bandwidth — split into inbound + outbound (with respect to
                    edge WAN). RX is traffic arriving at the edge (downloads
                    from internet, uploads from clients); TX is traffic
                    leaving the edge. Combined number kept on hover for users
                    who want the aggregate. */}
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Bandwidth</div>
                    <div className="flex items-center space-x-1" title={`Total: ${formatBandwidthMbps(data.bandwidthMbps).value} ${formatBandwidthMbps(data.bandwidthMbps).unit}`}>
                        <ArrowDownRight className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-base font-semibold text-gray-900">
                            {formatBandwidthMbps(data.rxBandwidthMbps).value}
                            <span className="text-xs font-normal text-gray-500 ml-1">{formatBandwidthMbps(data.rxBandwidthMbps).unit}</span>
                        </span>
                        <span className="text-xs text-gray-400 px-1">in</span>
                    </div>
                    <div className="flex items-center space-x-1 mt-1">
                        <ArrowUpRight className="w-4 h-4 text-success-500 flex-shrink-0" />
                        <span className="text-base font-semibold text-gray-900">
                            {formatBandwidthMbps(data.txBandwidthMbps).value}
                            <span className="text-xs font-normal text-gray-500 ml-1">{formatBandwidthMbps(data.txBandwidthMbps).unit}</span>
                        </span>
                        <span className="text-xs text-gray-400 px-1">out</span>
                    </div>
                    {sparklineData && (
                        <div className="h-8 mt-2">
                            <Sparkline data={sparklineData} />
                        </div>
                    )}
                </div>

                {/* Total Traffic */}
                <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">Total Traffic</div>
                    <div className="text-2xl font-bold text-gray-900 mt-1">{formatBytes(data.totalBytes)}</div>
                    <div className="text-xs text-gray-500 mt-1">
                        {formatNumber(data.totalPackets)} packets
                    </div>
                </div>
            </div>

            {/* Flows & Export Status */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                <div className="flex items-center space-x-4">
                    <div>
                        <div className="text-xs text-gray-500">Total Flows</div>
                        <div className="text-lg font-semibold text-gray-900">{formatNumber(data.totalFlows)}</div>
                    </div>
                </div>
                {data.flowExportActive > 0 && (
                    <div className="flex items-center space-x-1 px-2 py-1 bg-success-100 text-success-700 rounded-full text-xs font-medium">
                        <CheckCircle className="w-3 h-3" />
                        <span>Flow Export Active</span>
                    </div>
                )}
            </div>

            {/* Protocol Breakdown */}
            {data.protocolBreakdown.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Protocol Distribution</h3>
                    <div className="space-y-2">
                        {data.protocolBreakdown.slice(0, 4).map((proto) => (
                            <div key={proto.protocol} className="flex items-center">
                                <span className="w-12 text-xs text-gray-600 font-medium">{proto.protocol}</span>
                                <div className="flex-1 mx-2">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div
                                            className="bg-primary-500 h-2 rounded-full transition-all"
                                            style={{ width: `${Math.min(proto.percentage, 100)}%` }}
                                        />
                                    </div>
                                </div>
                                <span className="w-10 text-xs text-gray-600 text-right">{proto.percentage}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrafficOverviewCard;
